/**
 * Sesión B WF3 — Universal Requests / Solicitudes approval center.
 *
 * Merges every internal + external approval stream into one table:
 *   - EXTERNAL  (kind=external)  → public website contact-form leads
 *   - INTERNAL  (kind=internal)  → client_creation | suite_activation |
 *                                  evaluation_request (any suite)
 *
 * Routing is data-driven via REQUEST_HANDLERS — a single map keyed by
 * `request_type` provides get / postMessage / approve / reject / icon for
 * every request type. Adding a new suite (10th, 11th, …) requires ZERO
 * changes here: every suite creates an `evaluation_request` and inherits
 * the same lifecycle automatically.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableHeader, TableHead, TableBody, TableRow, TableCell,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Search, Inbox, ExternalLink, MessageSquare, Building2, BadgeCheck, AlertCircle, FileText, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { adminApi, requestsApi } from '@/lib/adminApi';
import { useApp } from '@/context/AppContext';
import { parseApiDate } from '@/lib/utils';
import { bizDate } from '@/lib/dates';
import ConversationThread from '@/components/admin/ConversationThread';

const STATUS_TONE = {
  new:       'bg-info/10 text-info border-info/30',
  contacted: 'bg-warning/10 text-warning border-warning/30',
  closed:    'bg-success/10 text-success border-success/30',
  pending:   'bg-warning/10 text-warning border-warning/30',
  approved:  'bg-success/10 text-success border-success/30',
  rejected:  'bg-destructive/10 text-destructive border-destructive/30',
  needs_revision: 'bg-destructive/10 text-destructive border-destructive/30',
  cancellation_requested: 'bg-destructive/10 text-destructive border-destructive/30',
  cancelled: 'bg-muted text-muted-foreground border-muted',
  submitted_for_review: 'bg-info/10 text-info border-info/30',
  info_required: 'bg-info/10 text-info border-info/30',
  closed_duplicate: 'bg-muted text-muted-foreground border-muted',
  closed_completed: 'bg-muted text-muted-foreground border-muted',
};

const KIND_TONE = {
  external: 'bg-accent/10 text-accent border-accent/30',
  internal: 'bg-primary/10 text-primary border-primary/30',
};

const PRIORITY_TONE = {
  low:      'bg-muted text-muted-foreground border-border',
  normal:   'bg-muted text-muted-foreground border-border',
  medium:   'bg-info/10 text-info border-info/30',
  high:     'bg-warning/10 text-warning border-warning/30',
  urgent:   'bg-destructive/10 text-destructive border-destructive/30',
  critical: 'bg-destructive/10 text-destructive border-destructive/30',
};

const AGING_TONE = {
  ok:    'text-muted-foreground',
  amber: 'text-warning font-semibold',
  red:   'text-destructive font-semibold',
};

// Approval Center Phase A — request types whose priority/due-date can be
// triaged by hm_admin (mirrors services/approval_center.py TRIAGEABLE).
// Users-Nav UAT: config_change excluded — priority/target-date are not
// relevant for configuration governance (dialog stays lean).
const TRIAGEABLE_TYPES = new Set(['lead', 'client_creation', 'suite_activation', 'evaluation_request', 'competency_request', 'job_profile_request', 'program_activation']);

// Sprint Governance — no bulk approval for governance requests (founder rule).
const BULK_TYPES = new Set(['client_creation', 'suite_activation']);

// Mirrors services/approval_center.py PENDING_STATUSES.
const PENDING_SET = new Set(['pending', 'new', 'contacted', 'in_review', 'pending_admin_review', 'submitted_for_review', 'cancellation_requested']);

// Best-effort provenance stamp: decisions made from the center UI.
const logCenterActivity = (request_type, request_id, action) => {
  api.post('/admin/approval-center/activity', { request_type, request_id, action }).catch(() => {});
};

// ---------------------------------------------------------------------------
// REQUEST_HANDLERS — the single source of truth for how each request_type
// loads details, posts messages, and moves through approve/reject. Any new
// request_type only needs a new entry here; the JSX below is data-driven.
// ---------------------------------------------------------------------------
const REQUEST_HANDLERS = {
  lead: {
    icon: ExternalLink,
    get: async (id) => {
      const arr = await api.get('/admin/leads').then((r) => r.data);
      return (arr || []).find((l) => l.id === id) || null;
    },
    postMessage: (id, body) => requestsApi.postLeadMessage(id, body),
    // Leads use status transitions, not approve/reject.
    approve: null,
    reject: null,
  },
  client_creation: {
    icon: Building2,
    get: (id) => requestsApi.getClientRequest(id),
    postMessage: (id, body) => requestsApi.postClientRequestMessage(id, body),
    approve: (id, opts) => requestsApi.approveClientRequest(id, opts || {}),
    reject: (id, reason) => requestsApi.rejectClientRequest(id, reason),
  },
  suite_activation: {
    icon: BadgeCheck,
    get: (id) => requestsApi.getSuiteRequest(id),
    postMessage: (id, body) => requestsApi.postSuiteRequestMessage(id, body),
    approve: (id) => requestsApi.approveSuiteRequest(id),
    reject: (id, reason) => requestsApi.rejectSuiteRequest(id, reason),
  },
  evaluation_request: {
    icon: BadgeCheck,
    get: (id) => requestsApi.getEvaluationRequest(id),
    postMessage: (id, body) => requestsApi.postEvaluationRequestMessage(id, body),
    approve: (id) => requestsApi.approveEvaluationRequest(id),
    reject: (id, reason) => requestsApi.rejectEvaluationRequest(id, reason),
  },
  // Founder UAT — strategic analysis requests (Climara / TalentIntelligence)
  // reach the HM Advisor via the Approval Center like every other request.
  strategic_analysis: {
    icon: BadgeCheck,
    get: (id) => requestsApi.getStrategicRequest(id),
    postMessage: (id, body) => requestsApi.postStrategicRequestMessage(id, body),
    approve: (id) => requestsApi.approveStrategicRequest(id),
    reject: (id, reason) => requestsApi.rejectStrategicRequest(id, reason),
  },
  // Approval Center Phase A — TalentHeart program activation (inline decision).
  program_activation: {
    icon: BadgeCheck,
    get: (id) => api.get(`/admin/theart/program-activation-requests/${id}`).then((r) => r.data),
    postMessage: (id, body) => api.post(`/admin/theart/program-activation-requests/${id}/messages`, { body }).then((r) => r.data),
    approve: (id) => api.post(`/admin/theart/program-activation-requests/${id}/approve`, {}).then((r) => r.data),
    reject: (id, reason) => api.post(`/admin/theart/program-activation-requests/${id}/reject`, { reason }).then((r) => r.data),
  },
  // Sprint Governance — client config change (inline decision, no bulk).
  config_change: {
    icon: Building2,
    get: (id) => api.get(`/admin/config-change-requests/${id}`).then((r) => r.data),
    postMessage: (id, body) => api.post(`/admin/config-change-requests/${id}/messages`, { body }).then((r) => r.data),
    approve: (id) => api.post(`/admin/config-change-requests/${id}/approve`).then((r) => r.data),
    reject: (id, reason) => api.post(`/admin/config-change-requests/${id}/reject`, { reason }).then((r) => r.data),
  },
  // Founder UAT Wave B — Climara campaign requests are fully operational in
  // the center: conversation + Approve & Launch (needs questionnaire, handled
  // by a dedicated footer block) + Request Changes + Reject + Open in Climara.
  climara_campaign: {
    icon: BadgeCheck,
    get: (id) => adminApi.climaraGetCampaignRequest(id),
    postMessage: (id, body) => adminApi.climaraCampaignRequestMessage(id, body),
    approve: null, // dedicated footer (questionnaire required)
    reject: (id, reason) => adminApi.climaraRejectCampaignRequest(id, reason),
  },
  // Founder UAT — Assessment Library review: the Approval Center is the
  // operational inbox. Quick summary + Approve & Publish + Reject inline;
  // "View in Assessment Studio" deep link for full read-only inspection.
  // Reject routes through the SAME governance endpoint used by the Library
  // page (request-changes → needs_revision + review_comments audit trail).
  library_review: {
    icon: FileText,
    get: async (id) => {
      const d = await api.get(`/admin/questionnaires/${id}`).then((r) => r.data);
      return { ...(d?.questionnaire || {}), versions: d?.versions || [] };
    },
    postMessage: null,
    approve: null, // dedicated footer (approve + publish chain)
    reject: (id, reason) => api.post(`/admin/questionnaires/${id}/request-changes`, { reason }).then((r) => r.data),
  },
};
const getHandler = (rt) => REQUEST_HANDLERS[rt] || null;

// UAT A1 — a recruitment evaluation_request without unambiguous candidate
// identity (name + email, or linked collaborators) must not be decidable.
const evreqIdentityMissing = (open, detail) => {
  if (open?.request_type !== 'evaluation_request') return false;
  const raw = detail?.raw || open?.raw || {};
  if (raw.flow !== 'recruitment') return false;
  if ((raw.collaborator_ids || []).length || (raw.collaborators || []).length) return false;
  const c = raw.candidate || {};
  const name = (raw.candidate_name || c.name || '').trim();
  const email = (raw.candidate_email || c.email || '').trim();
  return !(name && email);
};

// Given the raw request document, resolve a human-readable candidate label.
// Handles all evaluation_request flows uniformly:
//   - recruitment flow  → raw.candidate_name (single external candidate)
//   - internal flow     → raw.collaborators[*].name (one or more employees)
//   - client_creation   → raw.contact_name
//   - lead / other      → raw.name || —
const resolveCandidateLabel = (raw) => {
  if (!raw) return '—';
  if (raw.candidate?.name) return raw.candidate.name;
  if (raw.candidate_name) return raw.candidate_name;
  const collabs = Array.isArray(raw.collaborators) ? raw.collaborators : [];
  if (collabs.length) {
    const names = collabs
      .map((c) => c.name || c.email)
      .filter(Boolean)
      .join(', ');
    if (names) return names;
  }
  return raw.contact_name || raw.name || '—';
};

const fmt = (iso, lang) => {
  if (!iso) return '—';
  try {
    const d = parseApiDate(iso);
    return d ? d.toLocaleString(lang?.startsWith('es') ? 'es-ES' : 'en-US', { dateStyle: 'medium', timeStyle: 'short' }) : iso;
  }
  catch { return iso; }
};

export default function AdminLeads() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useApp();
  const isAdmin = user?.role === 'hm_admin';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // Sprint Users-Nav — deep-link support (?type=…&status=…&company_id=…) from
  // the pending-governance indicator on Clients pages.
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    q: '',
    kind: 'all',
    status: searchParams.get('status') || 'all',
    priority: 'all',
    type: searchParams.get('type') || 'all',
    companyId: searchParams.get('company_id') || '',
    advisor: 'all',
    overdue: false,
  });
  const clearDeepLink = () => {
    setFilters((f) => ({ ...f, type: 'all', companyId: '', status: 'all' }));
    setSearchParams({}, { replace: true });
  };
  // Approval Center Phase A — founder "My Queue" toggle (mine | team).
  const [scope, setScope] = useState('team');
  const [open, setOpen] = useState(null);      // currently-selected request row
  const [detail, setDetail] = useState(null);  // full request (with conversation)
  const [advisors, setAdvisors] = useState([]);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  // Phase 2D — "Anular solicitud" (governed void, never a hard delete)
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  // Sprint Governance — stale approve block (inline banner, deterministic UX).
  const [staleInfo, setStaleInfo] = useState(null);
  // Iter 41.37 — checkbox + temp-password reveal on client-creation approve.
  const [createClientAdmin, setCreateClientAdmin] = useState(false);
  const [tempPasswordReveal, setTempPasswordReveal] = useState(null); // { email, password, request_id }
  // TalentHeart 2026-06 — program-activation operational dialog
  // (request info / close as duplicate / close as completed).
  const [paDialog, setPaDialog] = useState(null); // { mode: 'info'|'close_duplicate'|'close_completed' }
  const [paText, setPaText] = useState('');
  const [paBusy, setPaBusy] = useState(false);
  // Iter 41.14 — bulk selection (hm_admin only). Keyed by "<request_type>:<id>"
  const [selected, setSelected] = useState(new Set());
  // UAT 2026-09 — workspace lifecycle: separate actionable work from history.
  const [viewTab, setViewTab] = useState('action'); // action | resolved | archived | all
  const [archBusy, setArchBusy] = useState(false);
  const ARCHIVABLE_TYPES = new Set(['lead', 'client_creation', 'suite_activation',
    'evaluation_request', 'competency_request', 'job_profile_request',
    'program_activation', 'climara_campaign', 'strategic_analysis', 'config_change']);
  const isRowArchived = (r) => !!(r.raw && r.raw.approval_archived);
  const isRowResolved = (r) => !PENDING_SET.has(r.status) && !isRowArchived(r);
  const archiveRow = async (r, restore = false) => {
    try {
      await api.post(`/admin/requests/${r.request_type}/${r.id}/${restore ? 'unarchive' : 'archive'}`);
      toast.success(restore
        ? t('pages.requests.archive.restored', { defaultValue: 'Request restored to history' })
        : t('pages.requests.archive.done', { defaultValue: 'Request archived — retrievable from the Archived tab' }));
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const archiveAllResolved = async (rows) => {
    setArchBusy(true);
    try {
      const eligible = rows.filter((r) => isRowResolved(r) && ARCHIVABLE_TYPES.has(r.request_type));
      await Promise.all(eligible.map((r) => api.post(`/admin/requests/${r.request_type}/${r.id}/archive`).catch(() => null)));
      toast.success(t('pages.requests.archive.bulkDone', { defaultValue: `${eligible.length} resolved requests archived`, n: eligible.length }));
      reload();
    } finally { setArchBusy(false); }
  };
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkReason, setBulkReason] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  // Wave B UAT — Climara campaign approval needs a certified questionnaire;
  // Request Changes needs an advisor note.
  const [climaraQs, setClimaraQs] = useState([]);
  const [climaraQId, setClimaraQId] = useState('');
  const [climaraChangesNote, setClimaraChangesNote] = useState('');
  const [climaraBusy, setClimaraBusy] = useState(false);
  // Founder UAT — Assessment Library review (Approve & Publish chain).
  const [libraryBusy, setLibraryBusy] = useState(false);
  // Iter 5 — publish-readiness envelope shown in the review dialog.
  const [libraryReadiness, setLibraryReadiness] = useState(null);

  const reload = async (sc = scope) => {
    setLoading(true);
    try {
      const data = await requestsApi.listAll({ scope: sc });
      setRows(data || []);
      // UAT polish — after approving/rejecting, rows leave the pending set;
      // prune them from the bulk selection so the action bar clears itself.
      setSelected((prev) => {
        if (!prev.size) return prev;
        const live = new Set((data || [])
          .filter((r) => r.kind === 'internal' && r.status === 'pending' && BULK_TYPES.has(r.request_type))
          .map((r) => `${r.request_type}:${r.id}`));
        const next = new Set([...prev].filter((k) => live.has(k)));
        return next.size === prev.size ? prev : next;
      });
    } catch (e) {
      toast.error(t('pages.requests.loadFailed'), { description: formatApiError(e) });
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(scope); }, [scope]);
  useEffect(() => {
    if (!isAdmin) return;
    requestsApi.listConsultants().then(setAdvisors).catch(() => {});
  }, [isAdmin]);

  // Load full doc whenever the user clicks a row (we need the raw embedded
  // doc to show all-fields + conversation). Data-driven via REQUEST_HANDLERS.
  useEffect(() => {
    if (!open) { setDetail(null); return; }
    setStaleInfo(null);
    let alive = true;
    const fetcher = async () => {
      try {
        const h = getHandler(open.request_type);
        if (!h?.get) { if (alive) setDetail(open); return; }
        const d = await h.get(open.id);
        if (alive) {
          setDetail({
            ...open,
            raw: d || open.raw,
            conversation: d?.conversation || open.conversation || [],
          });
        }
      } catch { if (alive) setDetail(open); }
    };
    fetcher();
    return () => { alive = false; };
  }, [open]);

  // Load published Climara questionnaires once a climara_campaign is opened.
  useEffect(() => {
    if (open?.request_type !== 'climara_campaign') { setClimaraQId(''); setClimaraChangesNote(''); return; }
    api.get('/admin/questionnaires', { params: { suite: 'climara', status: 'published' } })
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setClimaraQs(list.filter((q) => (q.suite_codes || []).includes('climara') && q.status === 'published'));
      })
      .catch(() => setClimaraQs([]));
  }, [open]);

  const climaraApprove = async () => {
    if (!open) return;
    if (!climaraQId) {
      toast.error(t('pages.requests.climara.pickQuestionnaire', { defaultValue: 'Select the certified questionnaire before launching.' }));
      return;
    }
    setClimaraBusy(true);
    try {
      const res = await adminApi.climaraApproveCampaignRequest(open.id, climaraQId, null);
      logCenterActivity('climara_campaign', open.id, 'approved');
      toast.success(t('pages.requests.climara.launched', {
        defaultValue: 'Campaign launched — {{n}} invitations sent', n: res.assignments,
      }), {
        duration: 10000,
        action: {
          label: t('pages.requests.climara.openConsole', { defaultValue: 'Open Campaign Console →' }),
          onClick: () => navigate('/admin/climara'),
        },
      });
      setOpen(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setClimaraBusy(false); }
  };

  const climaraRequestChanges = async () => {
    if (!open) return;
    if (climaraChangesNote.trim().length < 5) {
      toast.error(t('pages.requests.climara.changesNoteRequired', { defaultValue: 'Describe the requested changes (5+ characters).' }));
      return;
    }
    setClimaraBusy(true);
    try {
      await adminApi.climaraRequestChanges(open.id, climaraChangesNote.trim());
      logCenterActivity('climara_campaign', open.id, 'changes_requested');
      toast.success(t('pages.requests.climara.changesSent', { defaultValue: 'Changes requested — the client has been notified.' }));
      setClimaraChangesNote('');
      setOpen(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setClimaraBusy(false); }
  };

  // Iter 5 — run the publish-readiness validator when a library review opens
  // so HM Admin sees blocking errors vs warnings BEFORE deciding.
  useEffect(() => {
    if (open?.request_type !== 'library_review') { setLibraryReadiness(null); return; }
    let alive = true;
    api.post(`/admin/studio/assessments/${open.id}/validate`)
      .then((r) => { if (alive) setLibraryReadiness(r.data); })
      .catch(() => { if (alive) setLibraryReadiness(null); });
    return () => { alive = false; };
  }, [open]);

  // Founder UAT — library review: Approve & Publish in one action, reusing
  // the exact endpoints the Library page uses (approve → publish version /
  // studio publish). One state machine, one governance trail.
  const libraryApprovePublish = async () => {
    if (!open) return;
    setLibraryBusy(true);
    try {
      let versions = detail?.raw?.versions;
      if (!versions) {
        const d = await api.get(`/admin/questionnaires/${open.id}`).then((r) => r.data);
        versions = d?.versions || [];
      }
      await adminApi.approveEvaluation(open.id);
      const last = versions[versions.length - 1];
      try {
        if (last) await adminApi.publishVersion(open.id, last.id);
        else await adminApi.studioPublish(open.id, {
          publish_target: detail?.raw?.suite_codes?.[0] || open.raw?.suite_codes?.[0] || 'internal_library',
        });
      } catch (pubErr) {
        const d = pubErr?.response?.data?.detail;
        const pubMsg = typeof d === 'string' ? d : (d?.reason_es || d?.reason_en || d?.code || pubErr?.message || '');
        toast.warning(t('pages.requests.libraryReview.approvedBlockedToast', {
          defaultValue: 'Approved, but publishing was blocked — open it in the Studio to finish publishing.',
        }), { description: pubMsg });
        setOpen(null);
        reload();
        return;
      }
      logCenterActivity('library_review', open.id, 'approved_published');
      toast.success(t('pages.requests.libraryReview.approvedToast', {
        defaultValue: 'Assessment approved & published — now available in the library.',
      }));
      setOpen(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLibraryBusy(false); }
  };

  const filtered = useMemo(() => {
    const q = (filters.q || '').toLowerCase();
    return rows.filter((r) => {
      // UAT 2026-09 — workflow lifecycle tabs (Requires action / Resolved / Archived / All)
      const archived = !!(r.raw && r.raw.approval_archived);
      if (viewTab === 'archived') { if (!archived) return false; }
      else if (archived) return false;
      if (viewTab === 'action' && !PENDING_SET.has(r.status)) return false;
      if (viewTab === 'resolved' && PENDING_SET.has(r.status)) return false;
      if (filters.kind !== 'all' && r.kind !== filters.kind) return false;
      if (filters.status !== 'all') {
        // Founder governance sprint — "Rechazada" also matches library
        // reviews returned to the author (`needs_revision`).
        const matches = r.status === filters.status
          || (filters.status === 'rejected' && r.status === 'needs_revision');
        if (!matches) return false;
      }
      if (filters.priority !== 'all' && (r.priority || '') !== filters.priority) return false;
      if (filters.type !== 'all' && r.request_type !== filters.type) return false;
      if (filters.companyId && (r.raw?.company_id || '') !== filters.companyId) return false;
      if (filters.advisor !== 'all' && (r.assigned_advisor_id || '') !== filters.advisor) return false;
      if (filters.overdue && !(PENDING_SET.has(r.status) && (r.aging === 'red' || r.overdue))) return false;
      if (!q) return true;
      return [r.title, r.subtitle, r.email, r.requested_by_name, r.assigned_advisor_name]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q));
    });
  }, [rows, filters, viewTab]);

  const selectableRows = useMemo(
    () => filtered.filter((r) => isAdmin && r.kind === 'internal' && r.status === 'pending' && BULK_TYPES.has(r.request_type)),
    [filtered, isAdmin],
  );
  const selectableKeys = useMemo(
    () => new Set(selectableRows.map((r) => `${r.request_type}:${r.id}`)),
    [selectableRows],
  );
  const allSelected = selectableKeys.size > 0 && [...selectableKeys].every((k) => selected.has(k));
  const partialSelected = !allSelected && [...selectableKeys].some((k) => selected.has(k));

  const toggleRow = (r) => {
    const key = `${r.request_type}:${r.id}`;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => {
      if (allSelected) {
        const next = new Set(prev);
        selectableKeys.forEach((k) => next.delete(k));
        return next;
      }
      return new Set([...prev, ...selectableKeys]);
    });
  };
  const clearSelection = () => setSelected(new Set());

  const _grouped = () => {
    const cc = []; const sa = [];
    [...selected].forEach((k) => {
      const [rt, id] = k.split(':');
      if (rt === 'client_creation') cc.push(id);
      else if (rt === 'suite_activation') sa.push(id);
    });
    return { cc, sa };
  };

  const bulkApprove = async () => {
    const { cc, sa } = _grouped();
    if (cc.length + sa.length === 0) return;
    setBulkBusy(true);
    try {
      const ops = [];
      if (cc.length) ops.push(api.post('/admin/requests/bulk-approve', { request_type: 'client_creation', ids: cc }));
      if (sa.length) ops.push(api.post('/admin/requests/bulk-approve', { request_type: 'suite_activation', ids: sa }));
      const res = await Promise.all(ops);
      const approved = res.reduce((n, r) => n + (r.data?.approved_count || 0), 0);
      const failed = res.reduce((n, r) => n + (r.data?.failed_count || 0), 0);
      toast.success(t('pages.requests.bulk.approvedToast', { n: approved }));
      if (failed) toast.error(t('pages.requests.bulk.partialFailed', { n: failed }));
      clearSelection();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('pages.requests.bulk.failed'));
    } finally { setBulkBusy(false); }
  };

  const bulkReject = async () => {
    const reason = bulkReason.trim();
    if (!reason) { toast.error(t('pages.requests.bulk.reasonRequired')); return; }
    const { cc, sa } = _grouped();
    if (cc.length + sa.length === 0) return;
    setBulkBusy(true);
    try {
      const ops = [];
      if (cc.length) ops.push(api.post('/admin/requests/bulk-reject', { request_type: 'client_creation', ids: cc, reason }));
      if (sa.length) ops.push(api.post('/admin/requests/bulk-reject', { request_type: 'suite_activation', ids: sa, reason }));
      const res = await Promise.all(ops);
      const rejected = res.reduce((n, r) => n + (r.data?.rejected_count || 0), 0);
      toast.success(t('pages.requests.bulk.rejectedToast', { n: rejected }));
      setBulkRejectOpen(false);
      setBulkReason('');
      clearSelection();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('pages.requests.bulk.failed'));
    } finally { setBulkBusy(false); }
  };

  const STATUS_OPTIONS = ['all', 'new', 'contacted', 'closed', 'pending', 'approved', 'rejected', 'cancellation_requested', 'cancelled', 'pending_admin_review', 'submitted_for_review'];

  // Approval Center KPI strip (operational dashboard).
  const kpis = useMemo(() => {
    const pend = rows.filter((r) => PENDING_SET.has(r.status));
    const byPri = { critical: 0, high: 0, normal: 0 };
    let oldest = 0;
    pend.forEach((r) => {
      byPri[r.priority] = (byPri[r.priority] || 0) + 1;
      if ((r.waiting_hours || 0) > oldest) oldest = r.waiting_hours;
    });
    // Median time-to-decision over the last 7 days (client-side, on-load).
    const weekAgo = Date.now() - 7 * 24 * 3600 * 1000;
    const decidedHours = rows
      .map((r) => {
        const raw = r.raw || {};
        const decided = raw.decided_at ? new Date(raw.decided_at).getTime() : null;
        const created = r.created_at ? new Date(r.created_at).getTime() : null;
        return decided && created && decided >= weekAgo ? (decided - created) / 3600000 : null;
      })
      .filter((h) => h != null)
      .sort((a, b) => a - b);
    const median = decidedHours.length ? decidedHours[Math.floor(decidedHours.length / 2)] : null;
    return {
      pending: pend.length, byPri, oldest, median,
      overdue: pend.filter((r) => r.aging === 'red' || r.overdue).length,
    };
  }, [rows]);

  const fmtWaiting = (h) => {
    if (h == null) return '—';
    return h >= 48 ? `${Math.round(h / 24)}d` : `${Math.round(h)}h`;
  };

  // Triage (hm_admin): priority override + due date on pending rows.
  const triage = async (patch) => {
    if (!open) return;
    try {
      await api.patch(`/admin/requests/${open.request_type}/${open.id}/triage`, patch);
      toast.success(t('pages.requests.triage.saved', { defaultValue: 'Triage updated' }));
      setOpen(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('common.error'));
    }
  };

  const sendMessage = async (body) => {
    if (!open) return;
    const h = getHandler(open.request_type);
    if (!h?.postMessage) {
      toast.error(t('pages.requests.sendFailed', { defaultValue: 'Cannot send message for this request type' }));
      return;
    }
    const msg = await h.postMessage(open.id, body);
    setDetail((d) => ({ ...d, conversation: [...(d?.conversation || []), msg] }));
    setRows((arr) => arr.map((r) => r.id === open.id ? { ...r, conversation: [...(r.conversation || []), msg] } : r));
  };

  // TalentHeart 2026-06 — internal HM Admin ↔ Advisor thread on
  // program-activation requests. Never visible to the client.
  const sendInternalMessage = async (body) => {
    if (!open) return;
    const { data: msg } = await api.post(
      `/admin/theart/program-activation-requests/${open.id}/internal-messages`,
      { body },
    );
    setDetail((d) => ({
      ...d,
      raw: { ...(d?.raw || {}), internal_conversation: [...(d?.raw?.internal_conversation || []), msg] },
    }));
  };

  const paAction = async () => {
    if (!open || !paDialog) return;
    setPaBusy(true);
    try {
      let res;
      if (paDialog.mode === 'info') {
        res = await api.post(
          `/admin/theart/program-activation-requests/${open.id}/request-info`,
          { body: paText.trim() },
        );
      } else {
        res = await api.post(
          `/admin/theart/program-activation-requests/${open.id}/close`,
          {
            reason_code: paDialog.mode === 'close_duplicate' ? 'duplicate' : 'completed',
            reason: paText.trim() || null,
          },
        );
      }
      const fresh = res.data;
      setOpen((o) => (o ? { ...o, status: fresh.status } : o));
      setDetail((d) => (d ? { ...d, raw: { ...(d.raw || {}), ...fresh }, conversation: fresh.conversation || d.conversation } : d));
      setRows((arr) => arr.map((r) => (r.id === open.id ? { ...r, status: fresh.status, raw: { ...(r.raw || {}), ...fresh } } : r)));
      setPaDialog(null);
      setPaText('');
      toast.success(t('pages.requests.pa.actionDone', { defaultValue: 'Request updated' }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setPaBusy(false); }
  };

  const approve = async () => {
    if (!open) return;
    const h = getHandler(open.request_type);
    if (!h?.approve) return;
    try {
      const opts = open.request_type === 'client_creation'
        ? { create_client_admin: !!createClientAdmin }
        : undefined;
      const res = await h.approve(open.id, opts);
      logCenterActivity(open.request_type, open.id, 'approved');
      if (open.request_type === 'client_creation' && createClientAdmin && res?.temp_password) {
        setTempPasswordReveal({
          email: open.email || open.raw?.contact_email,
          password: res.temp_password,
          request_id: open.id,
        });
      }
      // UAT Sprint 03 — Post-approval CTA. For an evaluation_request, the
      // advisor MUST proceed to configure the freshly-created draft(s) with
      // questionnaire + version + deadline before the evaluated user can
      // see them. Give them a one-click "Configure now →" that lands on
      // the Evaluations page pre-filtered on drafts for this suite+company.
      if (open.request_type === 'evaluation_request') {
        const raw = detail?.raw || open.raw || {};
        const suite = raw.suite_code || '';
        const companyId = raw.company_id || open.company_id || '';
        const qs = new URLSearchParams({ status: 'draft' });
        if (suite) qs.set('suite', suite);
        if (companyId) qs.set('company_id', companyId);
        toast.success(t('pages.requests.approved'), {
          duration: 10000,
          description: t('pages.requests.approvedConfigureHint', {
            defaultValue: 'The draft assignment is ready to be configured with questionnaire, version and deadline.',
          }),
          action: {
            label: t('pages.requests.configureNow', { defaultValue: 'Configure now →' }),
            onClick: () => navigate(`/admin/evaluations?${qs.toString()}`),
          },
        });
      } else {
        toast.success(t('pages.requests.approved'));
      }
      setCreateClientAdmin(false);
      setOpen(null);
      reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // UAT A1 — server-side identity guard for recruitment requests.
      if (detail === 'candidate_identity_missing') {
        toast.error(t('pages.requests.identityMissing', {
          defaultValue: 'This request has no validly linked candidate (name and email are required). It cannot be approved or rejected until the candidate data is corrected.',
        }));
        return;
      }
      // Sprint Governance — staleness BLOCKS approval (founder rule):
      // show an inline banner in the dialog with a one-click refresh.
      if (open.request_type === 'config_change' && detail?.code === 'STALE_REQUEST') {
        setStaleInfo(detail);
        return;
      }
      // Founder UAT TC-016/017 — business-oriented bilingual message.
      if (detail === 'profile_has_no_linked_questionnaires') {
        toast.error(t('pages.requests.errors.profileNoAssessments', {
          defaultValue: 'This competency profile has no linked published assessments yet. Link at least one assessment in the Evaluation Library before approving.',
        }));
        return;
      }
      toast.error(typeof detail === 'string' ? detail : t('pages.requests.approveFailed'));
    }
  };

  // Sprint Governance — re-baseline a stale config change to current values.
  const refreshConfigRequest = async () => {
    if (!open?.id) return;
    try {
      const fresh = await api.post(`/admin/config-change-requests/${open.id}/refresh`).then((r) => r.data);
      setStaleInfo(null);
      toast.success(t('governance.refreshed', { defaultValue: 'Request refreshed against current values' }));
      if (fresh?.status !== 'pending') {
        // refresh emptied the request → auto-cancelled
        setOpen(null);
        reload();
        return;
      }
      setDetail((d) => ({ ...(d || open), raw: fresh, conversation: fresh.conversation || [] }));
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || t('common.error'));
    }
  };

  // UAT — P1 Evaluation Wizard Entry Point: permanent deep link from an
  // approved evaluation_request to the Evaluations draft view (same URL as
  // the post-approval toast shortcut).
  const goConfigureAssignments = () => {
    if (!open) return;
    const raw = detail?.raw || open.raw || {};
    const qs = new URLSearchParams({ status: 'draft' });
    const suite = raw.suite_code || open.suite_code || '';
    const companyId = raw.company_id || open.company_id || '';
    if (suite) qs.set('suite', suite);
    if (companyId) qs.set('company_id', companyId);
    setOpen(null);
    navigate(`/admin/evaluations?${qs.toString()}`);
  };

  const reject = async () => {
    if (!open) return;
    const h = getHandler(open.request_type);
    if (!h?.reject) return;
    if (open.request_type === 'library_review' && rejectReason.trim().length < 10) {
      toast.error(t('pages.requests.libraryReview.reasonTooShort', {
        defaultValue: 'Describe the rejection reason (10+ characters).',
      }));
      return;
    }
    try {
      await h.reject(open.id, rejectReason);
      logCenterActivity(open.request_type, open.id, 'rejected');
      toast.success(open.request_type === 'library_review'
        ? t('pages.requests.libraryReview.rejectedToast', { defaultValue: 'Returned to the author as "Needs revision" with your comments.' })
        : t('pages.requests.rejected'));
      setRejectOpen(false);
      setRejectReason('');
      setOpen(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('pages.requests.rejectFailed'));
    }
  };

  // Phase 2D — "Anular solicitud": governed void of a still-pending request.
  // Mandatory reason; record preserved; backend enforces permission + lifecycle.
  const cancelRequest = async () => {
    if (!open) return;
    if (cancelReason.trim().length < 5) {
      toast.error(t('pages.requests.cancelReasonRequired', { defaultValue: 'Indique el motivo de la anulación (mínimo 5 caracteres).' }));
      return;
    }
    try {
      await api.post(`/admin/requests/${open.request_type}/${open.id}/cancel`, { reason: cancelReason.trim() });
      logCenterActivity(open.request_type, open.id, 'cancelled');
      toast.success(t('pages.requests.cancelDone', { defaultValue: 'Solicitud anulada — el registro queda preservado en el historial.' }));
      setCancelOpen(false);
      setCancelReason('');
      setOpen(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || t('common.error'));
    }
  };

  // Founder UAT Wave 2 — decide on a client cancellation request. Approve →
  // status 'cancelled'; reject → back to 'pending'. Record never deleted.
  const decideCancellation = async (ok) => {
    if (!open) return;
    try {
      await requestsApi.cancellationDecision(open.id, ok);
      logCenterActivity('evaluation_request', open.id, ok ? 'cancellation_approved' : 'cancellation_rejected');
      toast.success(ok
        ? t('pages.requests.cancellationApproved', { defaultValue: 'Cancellation approved — request cancelled (record preserved).' })
        : t('pages.requests.cancellationRejected', { defaultValue: 'Cancellation rejected — request returned to pending.' }));
      setOpen(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('common.error', { defaultValue: 'Something went wrong' }));
    }
  };

  const updateLeadStatus = async (status) => {
    if (!open) return;
    try {
      const upd = await requestsApi.patchLead(open.id, { status });
      toast.success(t('pages.adminLeads.statusUpdated'));
      setRows((arr) => arr.map((r) => r.id === open.id ? { ...r, status: upd.status } : r));
      setDetail((d) => d ? { ...d, status: upd.status, raw: upd } : d);
      setOpen((o) => o ? { ...o, status: upd.status, raw: upd } : o);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('pages.adminLeads.statusUpdateFailed'));
    }
  };

  const assignLeadToAdvisor = async (advisorId) => {
    if (!open) return;
    try {
      const upd = await requestsApi.patchLead(open.id, { assigned_advisor_id: advisorId || '' });
      toast.success(t('pages.requests.advisorAssigned'));
      setRows((arr) => arr.map((r) => r.id === open.id ? { ...r, assigned_advisor_id: upd.assigned_advisor_id, assigned_advisor_name: upd.assigned_advisor_name, raw: upd } : r));
      setDetail((d) => d ? { ...d, assigned_advisor_id: upd.assigned_advisor_id, assigned_advisor_name: upd.assigned_advisor_name, raw: upd } : d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('common.error'));
    }
  };

  const renderRequestTypeBadge = (rt) => (
    <Badge variant="outline" className="text-xs">
      {t(`pages.requests.requestType.${rt}`, { defaultValue: rt })}
    </Badge>
  );

  return (
    <>
      <Topbar title={t('pages.requests.title')} subtitle={t('pages.requests.subtitle')} />
      <div className="p-8 space-y-6">
        {/* Approval Center KPI strip (operational dashboard) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ac-kpi-strip">
          <Card className="p-4" data-testid="ac-kpi-pending">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.requests.kpi.pending')}</p>
            <p className="text-2xl font-bold mt-1 tabular-nums">{loading ? '…' : kpis.pending}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t('pages.requests.kpi.byPriority')}:{' '}
              <span className="text-destructive font-semibold">{kpis.byPri.critical}</span>
              {' / '}
              <span className="text-warning font-semibold">{kpis.byPri.high}</span>
              {' / '}
              <span className="font-semibold">{kpis.byPri.normal}</span>
            </p>
          </Card>
          <Card className="p-4" data-testid="ac-kpi-oldest">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.requests.kpi.oldest')}</p>
            <p className="text-2xl font-bold mt-1 tabular-nums">{loading ? '…' : fmtWaiting(kpis.oldest || null)}</p>
          </Card>
          <Card className="p-4" data-testid="ac-kpi-median">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.requests.kpi.median')}</p>
            <p className="text-2xl font-bold mt-1 tabular-nums">{loading ? '…' : (kpis.median != null ? fmtWaiting(kpis.median) : '—')}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t('pages.requests.kpi.medianHint')}</p>
          </Card>
          <Card
            className={`p-4 cursor-pointer transition-colors hover:border-destructive/50 ${filters.overdue ? 'ring-2 ring-destructive border-destructive/60' : ''}`}
            data-testid="ac-kpi-overdue"
            onClick={() => setFilters((f) => ({ ...f, overdue: !f.overdue }))}
            title={t('pages.requests.kpi.overdueClickHint', { defaultValue: 'Click to filter expired items' })}
          >
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.requests.kpi.overdue')}</p>
            <p className={`text-2xl font-bold mt-1 tabular-nums ${kpis.overdue > 0 ? 'text-destructive' : ''}`}>{loading ? '…' : kpis.overdue}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {filters.overdue
                ? t('pages.requests.kpi.overdueActive', { defaultValue: 'Filtering — click to clear ×' })
                : t('pages.requests.kpi.overdueClickHint', { defaultValue: 'Click to filter expired items' })}
            </p>
          </Card>
        </div>

        <Card className="p-4 space-y-3" data-testid="requests-filters-card">
          <div className="flex flex-wrap items-center gap-2" data-testid="requests-lifecycle-tabs">
            {[
              ['action', t('pages.requests.tabs.action', { defaultValue: 'Requiere acción' })],
              ['resolved', t('pages.requests.tabs.resolved', { defaultValue: 'Resueltas' })],
              ['archived', t('pages.requests.tabs.archived', { defaultValue: 'Archivadas' })],
              ['all', t('pages.requests.tabs.all', { defaultValue: 'Todas' })],
            ].map(([v, label]) => (
              <Button
                key={v} size="sm" variant={viewTab === v ? 'default' : 'outline'}
                className="h-8 rounded-full"
                onClick={() => setViewTab(v)}
                data-testid={`requests-tab-${v}`}
              >{label}</Button>
            ))}
            {isAdmin && viewTab === 'resolved' && filtered.some((r) => isRowResolved(r) && ARCHIVABLE_TYPES.has(r.request_type)) && (
              <Button size="sm" variant="outline" className="h-8 ml-auto gap-1.5" disabled={archBusy}
                onClick={() => archiveAllResolved(filtered)} data-testid="requests-archive-all-resolved">
                <Inbox className="h-3.5 w-3.5" />
                {t('pages.requests.archive.allResolved', { defaultValue: 'Archivar resueltas visibles' })}
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-60">
              <label className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.requests.searchLabel')}</label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={filters.q}
                  onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                  className="pl-9 h-9"
                  placeholder={t('pages.requests.searchPh')}
                  data-testid="requests-search"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.requests.col.kind')}</label>
              <Select value={filters.kind} onValueChange={(v) => setFilters((f) => ({ ...f, kind: v }))}>
                <SelectTrigger className="h-9 mt-1 w-36" data-testid="requests-kind-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all', { defaultValue: 'All' })}</SelectItem>
                  <SelectItem value="external">{t('pages.requests.kind.external')}</SelectItem>
                  <SelectItem value="internal">{t('pages.requests.kind.internal')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.requests.col.status')}</label>
              <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
                <SelectTrigger className="h-9 mt-1 w-36" data-testid="requests-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s === 'all' ? t('common.all', { defaultValue: 'All' }) : t(`pages.requests.status.${s}`, { defaultValue: s })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.requests.col.priority')}</label>
              <Select value={filters.priority} onValueChange={(v) => setFilters((f) => ({ ...f, priority: v }))}>
                <SelectTrigger className="h-9 mt-1 w-36" data-testid="requests-priority-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all', { defaultValue: 'All' })}</SelectItem>
                  {['normal', 'high', 'critical'].map((p) => (
                    <SelectItem key={p} value={p}>{t(`pages.requests.priority.${p}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* UAT-006 — filter the Approval Center by HM Advisor */}
            {isAdmin && (
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.requests.col.advisor')}</label>
                <Select value={filters.advisor} onValueChange={(v) => setFilters((f) => ({ ...f, advisor: v }))}>
                  <SelectTrigger className="h-9 mt-1 w-44" data-testid="requests-advisor-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('common.all', { defaultValue: 'All' })}</SelectItem>
                    {advisors.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Founder "My Queue" toggle */}
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.requests.scope.label')}</label>
              <div className="flex mt-1 rounded-md border border-border overflow-hidden w-fit" data-testid="ac-scope-toggle">
                <Button
                  size="sm"
                  variant={scope === 'team' ? 'default' : 'ghost'}
                  className="rounded-none h-9"
                  onClick={() => setScope('team')}
                  data-testid="ac-scope-team"
                >
                  {t('pages.requests.scope.team')}
                </Button>
                <Button
                  size="sm"
                  variant={scope === 'mine' ? 'default' : 'ghost'}
                  className="rounded-none h-9"
                  onClick={() => setScope('mine')}
                  data-testid="ac-scope-mine"
                >
                  {t('pages.requests.scope.mine')}
                </Button>
              </div>
            </div>
            <span
              className="ml-auto px-2 py-0.5 rounded-full bg-primary/10 text-primary font-mono text-xs"
              data-testid="requests-result-count"
            >
              {loading ? '…' : filtered.length}
            </span>
          </div>
          {/* Sprint Users-Nav — active deep-link filter chip */}
          {(filters.type !== 'all' || filters.companyId) && (
            <div className="flex items-center gap-2" data-testid="requests-deeplink-chip">
              <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30 gap-1.5 py-1">
                {(() => {
                  const companyName = filters.companyId
                    ? (rows.find((r) => (r.raw?.company_id || '') === filters.companyId)?.company_name || '')
                    : '';
                  return companyName
                    ? t('pages.requests.deepLink.configChangesFor', { defaultValue: 'Config changes · {{company}}', company: companyName })
                    : t('pages.requests.deepLink.configChanges', { defaultValue: 'Config changes' });
                })()}
                <button type="button" onClick={clearDeepLink} className="hover:opacity-70" data-testid="requests-deeplink-clear" aria-label={t('pages.requests.deepLink.clear', { defaultValue: 'Clear filter' })}>
                  ×
                </button>
              </Badge>
            </div>
          )}
        </Card>

        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                {isAdmin && (
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected ? true : (partialSelected ? 'indeterminate' : false)}
                      onCheckedChange={toggleAll}
                      disabled={selectableKeys.size === 0}
                      data-testid="bulk-select-all"
                      aria-label="select all"
                    />
                  </TableHead>
                )}
                <TableHead>{t('pages.requests.col.kind')}</TableHead>
                <TableHead>{t('pages.requests.col.title')}</TableHead>
                <TableHead>{t('pages.requests.col.company', { defaultValue: 'Company' })}</TableHead>
                <TableHead>{t('pages.requests.col.status')}</TableHead>
                <TableHead>{t('pages.requests.col.priority')}</TableHead>
                <TableHead>{t('pages.requests.col.waiting', { defaultValue: 'Waiting' })}</TableHead>
                {isAdmin && <TableHead>{t('pages.requests.col.advisor')}</TableHead>}
                <TableHead className="w-20 text-center">
                  <MessageSquare className="h-3.5 w-3.5 inline" />
                </TableHead>
                <TableHead>{t('pages.requests.col.created')}</TableHead>
                {isAdmin && <TableHead className="w-12" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (<TableRow><TableCell colSpan={isAdmin ? 10 : 7} className="text-center text-muted-foreground py-12">{t('common.loading')}</TableCell></TableRow>)}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={isAdmin ? 10 : 7} className="text-center text-muted-foreground py-16">
                  <Inbox className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  {t('pages.requests.empty')}
                </TableCell></TableRow>
              )}
              {!loading && filtered.map((r) => {
                const rowKey = `${r.request_type}:${r.id}`;
                const isSelectable = isAdmin && r.kind === 'internal' && r.status === 'pending' && BULK_TYPES.has(r.request_type);
                const isChecked = selected.has(rowKey);
                return (
                <TableRow
                  key={rowKey}
                  className="hover:bg-muted/30 cursor-pointer"
                  onClick={() => {
                    // Deep-link queue rows (founder decision): the center routes
                    // to the specialized workspace instead of opening a dialog.
                    if (!r.capabilities?.inline_decision && r.capabilities?.link) {
                      navigate(r.capabilities.link);
                      return;
                    }
                    setOpen(r);
                  }}
                  data-testid={`request-row-${r.id}`}
                >
                  {isAdmin && (
                    <TableCell onClick={(e) => e.stopPropagation()} className="w-10">
                      {isSelectable && (
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => toggleRow(r)}
                          data-testid={`bulk-select-${r.id}`}
                        />
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge variant="outline" className={`${KIND_TONE[r.kind]} text-xs uppercase tracking-wider w-fit`} data-testid={`request-kind-${r.kind}-${r.id}`}>
                        {t(`pages.requests.kind.${r.kind}`)}
                      </Badge>
                      {renderRequestTypeBadge(r.request_type)}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium min-w-[220px]">
                    {r.title || '—'}
                    {r.capabilities?.link && !r.capabilities?.inline_decision && (
                      <ExternalLink className="h-3 w-3 inline ml-1.5 text-muted-foreground" />
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-40 truncate">
                    {r.company_scope === 'global_library'
                      ? t('pages.requests.libraryReview.globalScope', { defaultValue: 'Biblioteca Global CTH' })
                      : (r.company_name || '—')}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_TONE[r.status] || 'bg-warning/10 text-warning border-warning/30'}>
                      {t(`pages.requests.status.${r.status}`, { defaultValue: r.status })}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {r.priority ? (
                      <Badge variant="outline" className={PRIORITY_TONE[r.priority]}>{t(`pages.requests.priority.${r.priority}`, { defaultValue: r.priority })}</Badge>
                    ) : '—'}
                  </TableCell>
                  <TableCell className={`text-xs tabular-nums ${AGING_TONE[r.aging] || ''}`} data-testid={`request-waiting-${r.id}`}>
                    {PENDING_SET.has(r.status) ? fmtWaiting(r.waiting_hours) : '—'}
                    {r.overdue && <span className="ml-1">⚠</span>}
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-sm">{r.assigned_advisor_name || '—'}</TableCell>
                  )}
                  <TableCell className="text-center text-xs text-muted-foreground">
                    {(r.conversation || []).length}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmt(r.created_at, i18n.language)}</TableCell>
                  {isAdmin && (
                    <TableCell onClick={(e) => e.stopPropagation()} className="w-12 text-right">
                      {ARCHIVABLE_TYPES.has(r.request_type) && (isRowArchived(r) ? (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                          onClick={() => archiveRow(r, true)} data-testid={`request-unarchive-${r.id}`}
                          title={t('pages.requests.archive.restore', { defaultValue: 'Restaurar' })}>
                          {t('pages.requests.archive.restore', { defaultValue: 'Restaurar' })}
                        </Button>
                      ) : (!PENDING_SET.has(r.status) && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-muted-foreground"
                          onClick={() => archiveRow(r)} data-testid={`request-archive-${r.id}`}
                          title={t('pages.requests.archive.action', { defaultValue: 'Archivar' })}>
                          {t('pages.requests.archive.action', { defaultValue: 'Archivar' })}
                        </Button>
                      )))}
                    </TableCell>
                  )}
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Iter 41.14 — Bulk action floating bar (hm_admin only). */}
      {isAdmin && selected.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-card border-2 border-primary/40 shadow-xl rounded-full px-5 py-2.5 flex items-center gap-3"
          data-testid="bulk-action-bar"
        >
          <span className="text-sm font-semibold" data-testid="bulk-count">
            {t('pages.requests.bulk.selected', { n: selected.size })}
          </span>
          <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="bulk-clear">
            {t('common.cancel')}
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setBulkRejectOpen(true)}
            disabled={bulkBusy}
            data-testid="bulk-reject-btn"
          >
            {t('pages.requests.bulk.reject')}
          </Button>
          <Button
            size="sm"
            onClick={bulkApprove}
            disabled={bulkBusy}
            data-testid="bulk-approve-btn"
            className="bg-success hover:bg-success/90 text-white"
          >
            {t('pages.requests.bulk.approve')}
          </Button>
        </div>
      )}

      {/* Bulk reject reason dialog */}
      <Dialog open={bulkRejectOpen} onOpenChange={setBulkRejectOpen}>
        <DialogContent data-testid="bulk-reject-dialog">
          <DialogHeader>
            <DialogTitle>{t('pages.requests.bulk.rejectReasonTitle', { n: selected.size })}</DialogTitle>
            <DialogDescription>{t('pages.requests.bulk.rejectReasonDesc')}</DialogDescription>
          </DialogHeader>
          <Input
            value={bulkReason}
            onChange={(e) => setBulkReason(e.target.value)}
            placeholder={t('pages.requests.rejectReasonPh')}
            data-testid="bulk-reject-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkRejectOpen(false)} disabled={bulkBusy}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={bulkReject} disabled={bulkBusy || !bulkReason.trim()} data-testid="bulk-reject-confirm">
              {t('pages.requests.bulk.reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="request-detail-dialog">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {(() => {
                    const Icon = getHandler(open.request_type)?.icon || BadgeCheck;
                    return <Icon className="h-4 w-4" />;
                  })()}
                  {open.title}
                </DialogTitle>
                <DialogDescription asChild>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={KIND_TONE[open.kind]}>{t(`pages.requests.kind.${open.kind}`)}</Badge>
                    {renderRequestTypeBadge(open.request_type)}
                    <Badge variant="outline" className={STATUS_TONE[open.status]}>{t(`pages.requests.status.${open.status}`, { defaultValue: open.status })}</Badge>
                    {open.priority && <Badge variant="outline" className={PRIORITY_TONE[open.priority]}>{t(`pages.requests.priority.${open.priority}`)}</Badge>}
                  </div>
                </DialogDescription>
              </DialogHeader>
              {/* UAT Sprint 03 · Universal identity header — data-driven so every
                  request_type surfaces candidate/position/company/suite the same
                  way. Falls back to `open.raw` when the fresh fetch hasn't
                  landed yet, so fields render immediately on open. */}
              {(() => {
                const raw = detail?.raw || open.raw || {};
                const isConfigChange = open.request_type === 'config_change';
                const isLibraryReview = open.request_type === 'library_review';
                const candidate = resolveCandidateLabel(raw);
                const position = raw.position || raw.vacancy || '—';
                const company = isLibraryReview
                  ? t('pages.requests.libraryReview.globalScope', { defaultValue: 'Biblioteca Global CTH' })
                  : (raw.company_name || open.company_name || raw.company || '—');
                const requestingAdvisor = raw.requested_by_name || open.requested_by_name || '—';
                const requestedSuite = raw.suite_code
                  ? t(`suite.names.${raw.suite_code}`, { defaultValue: raw.suite_code })
                  : ((raw.requested_suites || raw.suite_codes || []).length
                      ? (raw.requested_suites || raw.suite_codes).map((s) => t(`suite.names.${s}`, { defaultValue: s })).join(', ')
                      : '—');
                const fieldsUnderReview = (raw.changes || [])
                  .map((c) => t(`governance.fields.${c.field}`, { defaultValue: c.field }))
                  .join(', ') || '—';
                const dateSubmitted = raw.submitted_for_review_at || raw.created_at || open.created_at;
                const candEmail = raw.candidate?.email || raw.candidate_email;
                return (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 p-3 rounded-lg bg-muted/40 border" data-testid="request-detail-header">
                    {/* UAT Governance finding #4 — candidate/position/suite are not
                        applicable to config change requests; show the governed
                        fields under review instead. */}
                    {!isConfigChange && !isLibraryReview && (
                      <div data-testid="rd-header-candidate">
                        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.requests.detailHeader.candidate', { defaultValue: 'Candidate' })}</p>
                        <p className="text-sm font-medium">{candidate}</p>
                      </div>
                    )}
                    {!isConfigChange && !isLibraryReview && (
                      <div data-testid="rd-header-position">
                        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.requests.detailHeader.position', { defaultValue: 'Position' })}</p>
                        <p className="text-sm font-medium">{position}</p>
                      </div>
                    )}
                    <div data-testid="rd-header-company">
                      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.requests.detailHeader.company', { defaultValue: 'Company' })}</p>
                      <p className="text-sm font-medium">{company}</p>
                    </div>
                    <div data-testid="rd-header-requesting-advisor">
                      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.requests.detailHeader.requestingAdvisor', { defaultValue: 'Requesting advisor' })}</p>
                      <p className="text-sm font-medium">{requestingAdvisor}</p>
                    </div>
                    {isConfigChange ? (
                      <div data-testid="rd-header-fields-under-review">
                        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.requests.detailHeader.fieldsUnderReview', { defaultValue: 'Fields under review' })}</p>
                        <p className="text-sm font-medium">{fieldsUnderReview}</p>
                      </div>
                    ) : (
                      <div data-testid="rd-header-requested-suite">
                        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.requests.detailHeader.requestedSuite', { defaultValue: 'Requested suite' })}</p>
                        <p className="text-sm font-medium">{requestedSuite}</p>
                      </div>
                    )}
                    {!isConfigChange && !isLibraryReview && candEmail && (
                      <div data-testid="rd-header-candidate-email">
                        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.requests.detailHeader.candidateEmail', { defaultValue: 'Candidate email' })}</p>
                        <p className="text-sm font-medium break-all" title={candEmail}>{candEmail}</p>
                      </div>
                    )}
                    <div data-testid="rd-header-status">
                      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.requests.detailHeader.statusLabel', { defaultValue: 'Current status' })}</p>
                      <p className="text-sm font-medium">{t(`pages.requests.status.${open.status}`, { defaultValue: open.status })}</p>
                    </div>
                    <div data-testid="rd-header-date-submitted">
                      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('pages.requests.detailHeader.dateSubmitted', { defaultValue: 'Date submitted' })}</p>
                      <p className="text-sm font-medium">{dateSubmitted ? fmt(dateSubmitted, i18n.language) : '—'}</p>
                    </div>
                  </div>
                );
              })()}
              <div className="space-y-4 text-sm">
                {/* Lead details */}
                {open.request_type === 'lead' && detail?.raw && (
                  <div className="space-y-2">
                    {detail.raw.email && <p>📧 <a href={`mailto:${detail.raw.email}`} className="text-primary hover:underline">{detail.raw.email}</a></p>}
                    {detail.raw.company && <p>{t('pages.requests.col.company')}: {detail.raw.company}</p>}
                    <div className="p-3 rounded-md bg-muted/40 border whitespace-pre-wrap">{detail.raw.message}</div>
                  </div>
                )}
                {/* Client creation details */}
                {open.request_type === 'client_creation' && detail?.raw && (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p><b>{t('pages.requests.clientForm.country')}:</b> {detail.raw.country || '—'}</p>
                    <p><b>{t('pages.requests.clientForm.industry')}:</b> {detail.raw.industry || '—'}</p>
                    <p><b>{t('pages.requests.clientForm.contactName')}:</b> {detail.raw.contact_name}</p>
                    <p><b>{t('pages.requests.clientForm.contactEmail')}:</b> {detail.raw.contact_email}</p>
                    <p><b>{t('pages.requests.clientForm.employees')}:</b> {detail.raw.employees_count}</p>
                    <p><b>{t('pages.requests.requestedBy')}:</b> {detail.raw.requested_by_name}</p>
                    <div className="col-span-2">
                      <b>{t('pages.requests.clientForm.requestedSuites')}:</b>{' '}
                      {(detail.raw.requested_suites || []).map((s) => (
                        <Badge key={s} variant="outline" className="ml-1 text-xs">{t(`suite.names.${s}`, { defaultValue: s })}</Badge>
                      )) || '—'}
                    </div>
                    {detail.raw.notes && (
                      <div className="col-span-2 p-2 rounded-md bg-muted/40 border whitespace-pre-wrap text-xs">{detail.raw.notes}</div>
                    )}
                    {detail.raw.reject_reason && (
                      <div className="col-span-2 p-2 rounded-md bg-destructive/10 border border-destructive/20 text-xs">
                        <AlertCircle className="h-3.5 w-3.5 inline mr-1" />
                        <b>{t('pages.requests.rejectReason')}:</b> {detail.raw.reject_reason}
                      </div>
                    )}
                  </div>
                )}
                {/* Suite activation details */}
                {open.request_type === 'suite_activation' && detail?.raw && (
                  <div className="space-y-2 text-sm">
                    <p><b>{t('pages.requests.suiteForm.suite')}:</b> {t(`suite.names.${detail.raw.suite_code}`, { defaultValue: detail.raw.suite_code })}
                      <Badge variant="outline" className="ml-2 text-xs" data-testid="suite-request-type-badge">
                        {detail.raw.request_type === 'contract'
                          ? t('phase3.requests.typeContract', { defaultValue: 'New contract' })
                          : t('phase3.requests.typeActivation', { defaultValue: 'Activation' })}
                      </Badge>
                    </p>
                    <p><b>{t('pages.requests.suiteForm.requestedBy')}:</b> {detail.raw.requested_by_name}</p>
                    <div><b>{t('pages.requests.suiteForm.justification')}:</b>
                      <div className="p-2 rounded-md bg-muted/40 border whitespace-pre-wrap mt-1 text-xs">{detail.raw.justification}</div>
                    </div>
                    <div><b>{t('pages.requests.suiteForm.useCase')}:</b>
                      <div className="p-2 rounded-md bg-muted/40 border whitespace-pre-wrap mt-1 text-xs">{detail.raw.use_case}</div>
                    </div>
                    {detail.raw.reject_reason && (
                      <div className="p-2 rounded-md bg-destructive/10 border border-destructive/20 text-xs">
                        <AlertCircle className="h-3.5 w-3.5 inline mr-1" />
                        <b>{t('pages.requests.rejectReason')}:</b> {detail.raw.reject_reason}
                      </div>
                    )}
                  </div>
                )}

                {/* Evaluation request details — universal panel for every suite */}
                {open.request_type === 'evaluation_request' && (detail?.raw || open.raw) && (() => {
                  const raw = detail?.raw || open.raw;
                  const isRecruitment = raw.flow === 'recruitment' || raw.suite_code === 'talentcheck';
                  const collabs = Array.isArray(raw.collaborators) ? raw.collaborators : [];
                  return (
                    <div className="space-y-2 text-sm" data-testid="evaluation-request-detail">
                      <p><b>{t('pages.requests.suiteForm.suite', { defaultValue: 'Suite' })}:</b> {t(`suite.names.${raw.suite_code}`, { defaultValue: raw.suite_code })}</p>
                      {raw.department && <p><b>{t('pages.requests.detailHeader.department', { defaultValue: 'Department' })}:</b> {raw.department}</p>}
                      {raw.urgency && <div><b>{t('pages.requests.col.priority', { defaultValue: 'Urgency' })}:</b> <Badge variant="outline" className={PRIORITY_TONE[raw.urgency]}>{t(`pages.requests.priority.${raw.urgency}`, { defaultValue: raw.urgency })}</Badge></div>}

                      {isRecruitment ? (() => {
                        const cand = raw.candidate || {};
                        const cName = raw.candidate_name || cand.name;
                        const cEmail = raw.candidate_email || cand.email;
                        const cPhone = raw.candidate_phone || cand.phone;
                        const cCv = raw.candidate_cv_url || cand.cv_url;
                        return (
                        <div className="p-2 rounded-md bg-muted/40 border text-xs space-y-1" data-testid="evreq-recruitment-block">
                          <p className="font-semibold">{t('pages.requests.candidateDetails', { defaultValue: 'Candidate details' })}</p>
                          {cName && <p data-testid="evreq-candidate-name">👤 {cName}</p>}
                          {cEmail && <p data-testid="evreq-candidate-email">📧 <a href={`mailto:${cEmail}`} title={cEmail} className="text-primary hover:underline break-all">{cEmail}</a></p>}
                          {cPhone && <p>📞 {cPhone}</p>}
                          {cCv && <p>📎 <a href={cCv} target="_blank" rel="noreferrer" className="text-primary hover:underline">{t('pages.requests.viewCv', { defaultValue: 'View CV' })}</a></p>}
                          {raw.hiring_manager && <p><b>{t('pages.requests.hiringManager', { defaultValue: 'Hiring manager' })}:</b> {raw.hiring_manager}</p>}
                          {raw.due_date && <p><b>{t('pages.requests.dueDate', { defaultValue: 'Due date' })}:</b> {bizDate(raw.due_date, i18n.language)}</p>}
                          {(!cName || !cEmail) && (
                            <p className="text-destructive font-medium" data-testid="evreq-identity-missing">
                              ⚠ {t('pages.requests.identityMissing', { defaultValue: 'This request has no validly linked candidate (name and email are required). It cannot be approved or rejected until the candidate data is corrected.' })}
                            </p>
                          )}
                        </div>
                        );
                      })() : (
                        <div className="p-2 rounded-md bg-muted/40 border text-xs" data-testid="evreq-collaborators-block">
                          <p className="font-semibold mb-1">
                            {t('pages.requests.collaborators', { defaultValue: 'Collaborators to evaluate' })}
                            {collabs.length > 0 && <span className="ml-1 text-muted-foreground">({collabs.length})</span>}
                          </p>
                          {collabs.length === 0 ? (
                            <p className="text-muted-foreground">—</p>
                          ) : (
                            <ul className="list-disc pl-5 space-y-0.5">
                              {collabs.map((c, i) => (
                                <li key={c.user_id || c.email || i}>
                                  {c.name || '—'}{c.email ? ` · ${c.email}` : ''}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}

                      {/* Founder UAT TT-001 — TalentTrain business context */}
                      {raw.training && (
                        <div className="space-y-1 p-3 rounded-md bg-primary/5 border border-primary/20" data-testid="request-training-details">
                          <p className="font-semibold text-xs uppercase tracking-wider text-primary">
                            {t('pages.requests.training.title', { defaultValue: 'Training details' })}
                          </p>
                          <p><b>{t('pages.requests.training.objective', { defaultValue: 'Objective' })}:</b> {raw.training.objective || '—'}</p>
                          {raw.training.competencies && (
                            <p><b>{t('pages.requests.training.competencies', { defaultValue: 'Competencies to strengthen' })}:</b> {raw.training.competencies}</p>
                          )}
                          {raw.training.gap && (
                            <p><b>{t('pages.requests.training.gap', { defaultValue: 'Observed gap' })}:</b> {raw.training.gap}</p>
                          )}
                          {raw.training.expected_outcome && (
                            <p><b>{t('pages.requests.training.outcome', { defaultValue: 'Expected outcome' })}:</b> {raw.training.expected_outcome}</p>
                          )}
                          {raw.training.training_type && (
                            <p><b>{t('pages.requests.training.type', { defaultValue: 'Preferred type' })}:</b>{' '}
                              {t(`pages.clientRequests.trainingType.${raw.training.training_type}`, { defaultValue: raw.training.training_type })}</p>
                          )}
                          {raw.training.justification && (
                            <p><b>{t('pages.requests.training.justification', { defaultValue: 'Business justification' })}:</b> {raw.training.justification}</p>
                          )}
                        </div>
                      )}

                      {raw.notes && (
                        <div><b>{t('pages.requests.notes', { defaultValue: 'Notes' })}:</b>
                          <div className="p-2 rounded-md bg-muted/40 border whitespace-pre-wrap mt-1 text-xs">{raw.notes}</div>
                        </div>
                      )}
                      {raw.reject_reason && (
                        <div className="p-2 rounded-md bg-destructive/10 border border-destructive/20 text-xs">
                          <AlertCircle className="h-3.5 w-3.5 inline mr-1" />
                          <b>{t('pages.requests.rejectReason')}:</b> {raw.reject_reason}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Founder UAT — strategic analysis details (Climara / TI) */}
                {open.request_type === 'strategic_analysis' && (detail?.raw || open.raw) && (() => {
                  const raw = detail?.raw || open.raw;
                  return (
                    <div className="space-y-2 text-sm" data-testid="strategic-analysis-detail">
                      <div className="grid grid-cols-2 gap-2">
                        <p><b>{t('clientTalentIntelligence.strategicDialog.area', { defaultValue: 'Area' })}:</b>{' '}
                          {t(`clientTalentIntelligence.strategicDialog.areas.${raw.area}`, { defaultValue: raw.area || '—' })}</p>
                        <p><b>{t('clientTalentIntelligence.strategicDialog.period', { defaultValue: 'Period' })}:</b>{' '}
                          {t(`clientTalentIntelligence.strategicDialog.periods.${raw.period}`, { defaultValue: raw.period || '—' })}</p>
                      </div>
                      {raw.urgency && (
                        <div><b>{t('pages.requests.col.priority')}:</b>{' '}
                          <Badge variant="outline" className={PRIORITY_TONE[raw.urgency]}>{t(`pages.requests.priority.${raw.urgency}`, { defaultValue: raw.urgency })}</Badge>
                        </div>
                      )}
                      {raw.description && (
                        <div className="p-2 rounded-md bg-muted/40 border whitespace-pre-wrap text-xs" data-testid="strategic-analysis-description">{raw.description}</div>
                      )}
                      {raw.reject_reason && (
                        <div className="p-2 rounded-md bg-destructive/10 border border-destructive/20 text-xs">
                          <AlertCircle className="h-3.5 w-3.5 inline mr-1" />
                          <b>{t('pages.requests.rejectReason')}:</b> {raw.reject_reason}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Program activation details (Approval Center Phase A) */}
                {open.request_type === 'program_activation' && (detail?.raw || open.raw) && (() => {
                  const raw = detail?.raw || open.raw;
                  return (
                    <div className="space-y-2 text-sm" data-testid="program-activation-detail">
                      <p><b>{t('pages.requests.requestType.program_activation')}:</b> {raw.program_code
                        ? t(`clientTalentHeart.programsReco.items.${raw.program_code}.name`, { defaultValue: raw.program_code })
                        : '—'}</p>
                      {raw.urgency && (
                        <div><b>{t('pages.requests.col.priority')}:</b>{' '}
                          <Badge variant="outline" className={PRIORITY_TONE[raw.urgency]}>{t(`pages.requests.priority.${raw.urgency}`, { defaultValue: raw.urgency })}</Badge>
                        </div>
                      )}
                      {raw.notes && (
                        <div className="p-2 rounded-md bg-muted/40 border whitespace-pre-wrap text-xs">{raw.notes}</div>
                      )}
                      {raw.reject_reason && (
                        <div className="p-2 rounded-md bg-destructive/10 border border-destructive/20 text-xs">
                          <AlertCircle className="h-3.5 w-3.5 inline mr-1" />
                          <b>{t('pages.requests.rejectReason')}:</b> {raw.reject_reason}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Founder UAT — Assessment Library review: quick read-only summary */}
                {open.request_type === 'library_review' && (detail?.raw || open.raw) && (() => {
                  const raw = { ...(open.raw || {}), ...(detail?.raw || {}) };
                  const versions = raw.versions || [];
                  const last = versions[versions.length - 1] || null;
                  const LR = (k, dv) => t(`pages.requests.libraryReview.${k}`, { defaultValue: dv });
                  const metaRows = [
                    [LR('metaCode', 'Code'), raw.display_code || raw.code || '—', 'code'],
                    [LR('metaType', 'Type'), raw.evaluation_type || raw.assessment_type || '—', 'type'],
                    [LR('metaLanguage', 'Language'), raw.language || '—', 'language'],
                    [LR('metaSuite', 'Target suite'), (raw.suite_codes || []).map((s) => t(`suite.names.${s}`, { defaultValue: s })).join(', ') || '—', 'suite'],
                    [LR('metaDuration', 'Estimated duration'), raw.estimated_minutes ? `${raw.estimated_minutes} min` : '—', 'duration'],
                    [LR('metaVersion', 'Version'), last ? `v${last.version_number}` : '—', 'version'],
                    [LR('metaSections', 'Sections'), last ? (last.sections || []).length : '—', 'sections'],
                    [LR('metaQuestions', 'Questions'), last ? (last.questions || []).length : '—', 'questions'],
                  ];
                  return (
                    <div className="space-y-2 text-sm" data-testid="library-review-detail">
                      {/* Founder UAT post-168 (D) — duplicate-submission signal */}
                      {raw.duplicate_of_published && (
                        <div className="p-2.5 rounded-md border bg-amber-50 border-amber-300 text-amber-900 text-xs"
                             data-testid="library-review-duplicate-warning">
                          <p className="font-semibold flex items-center gap-1.5">
                            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                            {t('pages.requests.libraryReview.duplicateTitle', {
                              defaultValue: 'Possible duplicate of a published instrument',
                            })}
                          </p>
                          <p className="mt-1">
                            {t('pages.requests.libraryReview.duplicateBody', {
                              defaultValue: 'An instrument with the same name is already PUBLISHED in the library (code {{code}}). Verify before approving — if this submission is redundant, reject it as a duplicate instead of publishing a second copy.',
                              code: raw.duplicate_of_published.code || '—',
                            })}
                          </p>
                        </div>
                      )}
                      {/* Founder governance sprint — rejected reviews stay
                          visible with their persisted reason. */}
                      {open.status === 'needs_revision' && (
                        <div className="p-2.5 rounded-md border border-destructive/30 bg-destructive/5 text-xs"
                             data-testid="library-review-rejection-banner">
                          <p className="font-semibold text-destructive">
                            {t('pages.requests.libraryReview.rejectedState', { defaultValue: 'Rejected — returned to the author (needs changes)' })}
                          </p>
                          {raw.rejection_reason && (
                            <p className="mt-1 whitespace-pre-wrap">
                              <b>{t('pages.requests.libraryReview.rejectionReason', { defaultValue: 'Reason' })}:</b> {raw.rejection_reason}
                            </p>
                          )}
                        </div>
                      )}
                      {libraryReadiness && (() => {
                        const errs = libraryReadiness.severity_counts?.error || 0;
                        const warns = libraryReadiness.severity_counts?.warning || 0;
                        const blocked = !!libraryReadiness.blocks_publish;
                        const tone = blocked
                          ? 'bg-destructive/10 border-destructive/30 text-destructive'
                          : warns > 0
                            ? 'bg-warning/10 border-warning/30 text-warning'
                            : 'bg-success/10 border-success/30 text-success';
                        const label = blocked
                          ? t('pages.requests.libraryReview.readinessBlocked', { defaultValue: '{{n}} blocking errors — fix them in the Studio before publishing', n: errs })
                          : warns > 0
                            ? t('pages.requests.libraryReview.readinessWarnings', { defaultValue: 'Publishable — {{n}} warnings (non-blocking)', n: warns })
                            : t('pages.requests.libraryReview.readinessReady', { defaultValue: 'Ready to publish — no blocking errors' });
                        return (
                          <div className="space-y-1.5">
                            <div className={`p-2 rounded-md border text-xs font-semibold flex items-center gap-1.5 ${tone}`}
                                 data-testid="library-review-readiness">
                              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                              {label}
                            </div>
                            {/* UAT B5 — make the approval decision understandable
                                without leaving the Approval Center. */}
                            {((libraryReadiness.issues || (libraryReadiness.categories || []).flatMap((c) => c.issues || []))).filter((i) => i.severity === 'error').length > 0 && (
                              <ul className="p-2 rounded-md border border-destructive/20 bg-destructive/5 space-y-1"
                                  data-testid="library-review-blocking-list">
                                {((libraryReadiness.issues || (libraryReadiness.categories || []).flatMap((c) => c.issues || []))).filter((i) => i.severity === 'error').slice(0, 5).map((i, idx) => {
                                  const esUi = i18n.language?.startsWith('es');
                                  return (
                                    <li key={idx} className="text-xs">
                                      <p className="font-medium text-destructive">• {esUi ? (i.message_es || i.message) : i.message}</p>
                                      {(i.recommendation || i.recommendation_es) && (
                                        <p className="text-muted-foreground pl-2.5">{esUi ? (i.recommendation_es || i.recommendation) : i.recommendation}</p>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                            <Button
                              size="sm" variant="outline" className="h-7 text-xs gap-1"
                              onClick={() => navigate(`/admin/studio/edit/${raw.id || open.id}`)}
                              data-testid="library-review-open-studio"
                            >
                              {t('pages.requests.libraryReview.openStudio', { defaultValue: 'Open in Assessment Studio' })} →
                            </Button>
                          </div>
                        );
                      })()}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 p-3 rounded-md bg-muted/40 border text-xs">
                        {metaRows.map(([k, v, key]) => (
                          <p key={key} data-testid={`library-review-meta-${key}`}><b>{k}:</b> {v}</p>
                        ))}
                      </div>
                      {raw.objective && (
                        <div><b>{LR('objective', 'Objective')}:</b>
                          <div className="p-2 rounded-md bg-muted/40 border whitespace-pre-wrap mt-1 text-xs">{raw.objective}</div>
                        </div>
                      )}
                      {raw.description && (
                        <div><b>{LR('description', 'Description')}:</b>
                          <div className="p-2 rounded-md bg-muted/40 border whitespace-pre-wrap mt-1 text-xs">{raw.description}</div>
                        </div>
                      )}
                      {(raw.review_comments || []).length > 0 && (
                        <div className="p-2 rounded-md bg-warning/10 border border-warning/30 text-xs space-y-1" data-testid="library-review-history">
                          <p className="font-semibold">{LR('history', 'Previous review rounds')}</p>
                          {raw.review_comments.map((c, i) => (
                            <p key={c.id || i}>• {c.reason} — {c.actor_name || '—'} · {(c.at || '').slice(0, 10)}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Wave B UAT — Climara campaign request: complete operational detail */}
                {open.request_type === 'climara_campaign' && (detail?.raw || open.raw) && (() => {
                  const raw = detail?.raw || open.raw;
                  const audienceLabel = {
                    all: t('pages.requests.climara.audienceAll', { defaultValue: 'Whole company' }),
                    departments: t('pages.requests.climara.audienceDepts', { defaultValue: 'Selected departments' }),
                    employees: t('pages.requests.climara.audienceEmployees', { defaultValue: 'Specific employees' }),
                  }[raw.target_audience] || raw.target_audience;
                  return (
                    <div className="space-y-2 text-sm" data-testid="climara-campaign-detail">
                      <div className="grid grid-cols-2 gap-2">
                        <p><b>{t('pages.requests.climara.measurement', { defaultValue: 'Measurement' })}:</b> {raw.name}</p>
                        <p><b>{t('pages.requests.climara.wave', { defaultValue: 'Wave' })}:</b> {raw.wave_label}</p>
                        <p><b>{t('pages.requests.climara.audience', { defaultValue: 'Audience' })}:</b> {audienceLabel}</p>
                        <p><b>{t('pages.requests.climara.recipients', { defaultValue: 'Estimated recipients' })}:</b> {raw.invited_count}</p>
                        <p><b>{t('pages.requests.dueDate', { defaultValue: 'Deadline' })}:</b> {raw.deadline || '—'}</p>
                        <p><b>{t('pages.requests.climara.submitted', { defaultValue: 'Submitted' })}:</b> {(raw.created_at || '').slice(0, 10)}</p>
                      </div>
                      {(raw.department_names || []).length > 0 && (
                        <div data-testid="climara-detail-departments">
                          <b>{t('pages.requests.climara.departments', { defaultValue: 'Departments' })}:</b>{' '}
                          {(raw.department_names || []).map((d) => (
                            <Badge key={d} variant="outline" className="ml-1 text-xs">{d}</Badge>
                          ))}
                        </div>
                      )}
                      {raw.target_audience === 'employees' && (raw.recipients_preview || []).length > 0 && (
                        <div className="p-2 rounded-md bg-muted/40 border text-xs" data-testid="climara-detail-employees">
                          <p className="font-semibold mb-1">
                            {t('pages.requests.climara.employeesList', { defaultValue: 'Selected employees' })} ({raw.recipients_preview.length})
                          </p>
                          <ul className="list-disc pl-5 space-y-0.5 max-h-32 overflow-y-auto">
                            {raw.recipients_preview.map((u) => (
                              <li key={u.id}>{u.name || '—'}{u.email ? ` · ${u.email}` : ''}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {raw.anonymity_note && (
                        <div><b>{t('pages.requests.climara.anonNote', { defaultValue: 'Confidentiality note' })}:</b>
                          <div className="p-2 rounded-md bg-muted/40 border whitespace-pre-wrap mt-1 text-xs">{raw.anonymity_note}</div>
                        </div>
                      )}
                      {raw.notes && (
                        <div><b>{t('pages.requests.notes', { defaultValue: 'Notes' })}:</b>
                          <div className="p-2 rounded-md bg-muted/40 border whitespace-pre-wrap mt-1 text-xs">{raw.notes}</div>
                        </div>
                      )}
                      {raw.questionnaire_name && (
                        <p data-testid="climara-detail-questionnaire"><b>{t('pages.requests.climara.questionnaire', { defaultValue: 'Questionnaire' })}:</b> {raw.questionnaire_name}</p>
                      )}
                      {raw.changes_note && open.status === 'changes_requested' && (
                        <div className="p-2 rounded-md bg-info/10 border border-info/30 text-xs" data-testid="climara-detail-changes-note">
                          <b>{t('pages.requests.climara.changesNote', { defaultValue: 'Changes requested' })}:</b> {raw.changes_note}
                        </div>
                      )}
                      {raw.reject_reason && (
                        <div className="p-2 rounded-md bg-destructive/10 border border-destructive/20 text-xs">
                          <AlertCircle className="h-3.5 w-3.5 inline mr-1" />
                          <b>{t('pages.requests.rejectReason')}:</b> {raw.reject_reason}
                        </div>
                      )}
                      {['pending', 'changes_requested'].includes(open.status) && (
                        <div className="space-y-2 border-t pt-3" data-testid="climara-detail-actions">
                          <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground">
                              {t('pages.requests.climara.pickQuestionnaireLabel', { defaultValue: 'Certified questionnaire (required to launch)' })}
                            </label>
                            <Select value={climaraQId} onValueChange={setClimaraQId}>
                              <SelectTrigger className="mt-1 h-9" data-testid="climara-detail-q-select">
                                <SelectValue placeholder={t('pages.requests.climara.questionnairePh', { defaultValue: 'Select questionnaire…' })} />
                              </SelectTrigger>
                              <SelectContent>
                                {climaraQs.map((q) => <SelectItem key={q.id} value={q.id}>{q.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground">
                              {t('pages.requests.climara.changesNoteLabel', { defaultValue: 'Request changes (note visible to the client)' })}
                            </label>
                            <Textarea rows={2} value={climaraChangesNote}
                                      onChange={(e) => setClimaraChangesNote(e.target.value)}
                                      className="mt-1"
                                      placeholder={t('pages.requests.climara.changesNotePh', { defaultValue: 'E.g. adjust the audience or the deadline…' })}
                                      data-testid="climara-detail-changes-input" />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Sprint Governance — config change diff table */}
                {open.request_type === 'config_change' && (detail?.raw || open.raw) && (() => {
                  const raw = detail?.raw || open.raw;
                  const fmtVal = (v) => Array.isArray(v) ? (v.length ? v.join(', ') : '—') : String(v ?? '—');
                  return (
                    <div className="space-y-2 text-sm" data-testid="config-change-detail">
                      {raw.auto_approved && (
                        <Badge variant="outline" className="bg-info/10 text-info border-info/30 text-xs" data-testid="config-change-auto-approved">
                          {t('governance.autoApproved', { defaultValue: 'Auto-approved — HM Admin direct edit' })}
                        </Badge>
                      )}
                      <div className="rounded-md border overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-muted/40 text-left">
                            <tr>
                              <th className="px-3 py-2 font-semibold">{t('governance.diffField', { defaultValue: 'Field' })}</th>
                              <th className="px-3 py-2 font-semibold">{t('governance.diffFrom', { defaultValue: 'Current' })}</th>
                              <th className="px-3 py-2 font-semibold">{t('governance.diffTo', { defaultValue: 'Proposed' })}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {(raw.changes || []).map((ch) => (
                              <tr key={ch.field} data-testid={`config-diff-${ch.field}`}>
                                <td className="px-3 py-2 font-medium">{t(`governance.fields.${ch.field}`, { defaultValue: ch.field })}</td>
                                <td className="px-3 py-2 text-muted-foreground">{fmtVal(ch.from_label ?? ch.from)}</td>
                                <td className="px-3 py-2 font-semibold">{fmtVal(ch.to_label ?? ch.to)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {raw.reason && (
                        <div><b>{t('governance.reasonLabel', { defaultValue: 'Justification' })}:</b>
                          <div className="p-2 rounded-md bg-muted/40 border whitespace-pre-wrap mt-1 text-xs">{raw.reason}</div>
                        </div>
                      )}
                      {raw.reject_reason && (
                        <div className="p-2 rounded-md bg-destructive/10 border border-destructive/20 text-xs">
                          <AlertCircle className="h-3.5 w-3.5 inline mr-1" />
                          <b>{t('pages.requests.rejectReason')}:</b> {raw.reject_reason}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Sprint Governance — stale approve banner (blocks approval) */}
                {open.request_type === 'config_change' && staleInfo && (
                  <div className="p-3 rounded-md bg-destructive/10 border border-destructive/30 space-y-2" data-testid="config-change-stale-banner">
                    <p className="text-xs font-semibold text-destructive flex items-center gap-1.5">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                      {t('governance.staleTitle', { defaultValue: 'Request is stale — approval blocked' })}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('governance.staleDesc', {
                        defaultValue: 'Fields changed since the request was raised: {{fields}}. Refresh the request (re-baseline to current values) or reject it.',
                        fields: (staleInfo.stale_fields || []).map((s) => t(`governance.fields.${s.field}`, { defaultValue: s.field })).join(', '),
                      })}
                    </p>
                    <Button size="sm" variant="outline" onClick={refreshConfigRequest} data-testid="config-change-refresh-btn">
                      {t('governance.refreshRequest', { defaultValue: 'Refresh request' })}
                    </Button>
                  </div>
                )}

                {/* WF4 — Admin advisor assignment for external leads */}
                {isAdmin && open.request_type === 'lead' && (
                  <div className="border-t pt-3">
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.requests.assignAdvisor')}</label>
                    <Select
                      value={open.assigned_advisor_id || '__unassigned__'}
                      onValueChange={(v) => assignLeadToAdvisor(v === '__unassigned__' ? '' : v)}
                    >
                      <SelectTrigger className="mt-1" data-testid="lead-assign-advisor"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__unassigned__">{t('pages.requests.unassigned')}</SelectItem>
                        {advisors.map((a) => (
                          <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Approval Center Phase A — hm_admin triage (priority / due date) */}
                {isAdmin && TRIAGEABLE_TYPES.has(open.request_type) && ['pending', 'new'].includes(open.status) && (
                  <div className="border-t pt-3 flex flex-wrap items-end gap-3" data-testid="ac-triage">
                    <div>
                      <label className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.requests.triage.priority', { defaultValue: 'Priority' })}</label>
                      <Select onValueChange={(v) => triage({ priority: v })}>
                        <SelectTrigger className="h-9 mt-1 w-36" data-testid="ac-triage-priority"><SelectValue placeholder={open.priority || 'normal'} /></SelectTrigger>
                        <SelectContent>
                          {['normal', 'high', 'critical'].map((p) => (
                            <SelectItem key={p} value={p}>{t(`pages.requests.priority.${p}`, { defaultValue: p })}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-muted-foreground">{t('pages.requests.triage.dueDate', { defaultValue: 'Target resolution date' })}</label>
                      <Input
                        type="date"
                        className="h-9 mt-1 w-40"
                        defaultValue={(open.due_date || '').slice(0, 10)}
                        onBlur={(e) => { if (e.target.value !== (open.due_date || '').slice(0, 10)) triage({ due_date: e.target.value }); }}
                        data-testid="ac-triage-due-date"
                      />
                    </div>
                  </div>
                )}

                {open.request_type !== 'library_review' && (
                  <div className="border-t pt-3">
                    <ConversationThread
                      messages={detail?.conversation || open.conversation || []}
                      onSend={sendMessage}
                    />
                  </div>
                )}

                {/* TalentHeart 2026-06 — internal HM-only coordination thread */}
                {open.request_type === 'program_activation' && (
                  <div className="border-t pt-3" data-testid="pa-internal-thread-section">
                    <ConversationThread
                      variant="internal"
                      messages={detail?.raw?.internal_conversation || []}
                      onSend={sendInternalMessage}
                    />
                  </div>
                )}
              </div>
              <DialogFooter className="gap-2 flex-wrap">
                {open.request_type === 'lead' && (
                  <>
                    {['new', 'contacted', 'closed'].map((s) => (
                      <Button
                        key={s}
                        variant={open.status === s ? 'default' : 'outline'}
                        size="sm"
                        disabled={open.status === s}
                        onClick={() => updateLeadStatus(s)}
                        data-testid={`lead-set-status-${s}`}
                      >
                        {t(`pages.requests.status.${s}`)}
                      </Button>
                    ))}
                  </>
                )}
                {open.request_type === 'evaluation_request' && open.status === 'approved'
                  && (detail?.raw?.draft_assignment_count ?? 0) > 0 && (
                  <Button
                    size="sm"
                    onClick={goConfigureAssignments}
                    data-testid="request-configure-assignments-btn"
                  >
                    {t('pages.requests.configureAssignments', { defaultValue: 'Configure assignments →' })}
                  </Button>
                )}
                {open.request_type === 'evaluation_request' && open.status === 'approved' && ((detail?.raw || open.raw || {}).flow === 'recruitment') && (
                  <Button
                    size="sm"
                    className="bg-gradient-primary hover:opacity-90"
                    data-testid="evreq-open-config-btn"
                    onClick={async () => {
                      try {
                        const res = await adminApi.ensureEvaluationRequestDraft(open.id);
                        setOpen(null);
                        navigate(`/admin/evaluations/${res.assignment_id}`);
                      } catch (e2) {
                        toast.error(formatApiError(e2));
                      }
                    }}
                  >
                    {t('pages.requests.openConfiguration', { defaultValue: 'Open configuration →' })}
                  </Button>
                )}
                {/* TalentHeart 2026-06 — program-activation operational actions */}
                {isAdmin && open.request_type === 'program_activation'
                  && ['pending', 'info_required', 'approved'].includes(open.status) && (
                  <>
                    {['pending', 'info_required'].includes(open.status) && (
                      <Button variant="outline" size="sm"
                              onClick={() => { setPaText(''); setPaDialog({ mode: 'info' }); }}
                              data-testid="pa-request-info-btn">
                        {t('pages.requests.pa.requestInfo', { defaultValue: 'Request information' })}
                      </Button>
                    )}
                    <Button variant="outline" size="sm"
                            onClick={() => { setPaText(''); setPaDialog({ mode: 'close_duplicate' }); }}
                            data-testid="pa-close-duplicate-btn">
                      {t('pages.requests.pa.closeDuplicate', { defaultValue: 'Close — duplicate' })}
                    </Button>
                    {open.status === 'approved' && (
                      <Button variant="outline" size="sm"
                              onClick={() => { setPaText(''); setPaDialog({ mode: 'close_completed' }); }}
                              data-testid="pa-close-completed-btn">
                        {t('pages.requests.pa.closeCompleted', { defaultValue: 'Close — completed' })}
                      </Button>
                    )}
                  </>
                )}
                {/* Phase 2D — Anular solicitud (governed void; hm_admin or requester) */}
                {open.request_type !== 'lead' && open.request_type !== 'library_review'
                  && PENDING_SET.has(open.status)
                  && (isAdmin || (detail?.raw?.requested_by === user?.id)) && (
                  <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive"
                          onClick={() => setCancelOpen(true)} data-testid="request-cancel-btn">
                    <Ban className="h-3.5 w-3.5 mr-1.5" />
                    {t('pages.requests.cancelAction', { defaultValue: 'Anular solicitud' })}
                  </Button>
                )}
                {isAdmin && open.request_type !== 'lead' && open.status === 'pending' && getHandler(open.request_type)?.approve && !evreqIdentityMissing(open, detail) && (
                  <>
                    {open.request_type === 'client_creation' && (
                      <label className="flex items-center gap-2 text-sm mr-auto"
                             data-testid="approve-create-client-admin-label">
                        <Checkbox
                          checked={createClientAdmin}
                          onCheckedChange={(v) => setCreateClientAdmin(!!v)}
                          data-testid="approve-create-client-admin"
                        />
                        <span>
                          {t('pages.requests.createClientAdmin', {
                            defaultValue: 'Create Client Admin user on approval',
                          })}
                        </span>
                      </label>
                    )}
                    <Button variant="outline" size="sm" onClick={() => setRejectOpen(true)} data-testid="request-reject-btn">
                      {t('pages.requests.reject')}
                    </Button>
                    <Button size="sm" onClick={approve} data-testid="request-approve-btn">
                      {t('pages.requests.approve')}
                    </Button>
                  </>
                )}
                {/* UAT Sprint 03 — hm_advisor is now allowed to approve/reject
                    evaluation_request rows in their book (backend `_hm_any`
                    guard already accepts them). Client-creation and suite-
                    activation approvals stay admin-only for now. */}
                {!isAdmin && ['evaluation_request', 'strategic_analysis'].includes(open.request_type) && open.status === 'pending' && !evreqIdentityMissing(open, detail) && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setRejectOpen(true)} data-testid="request-reject-btn">
                      {t('pages.requests.reject')}
                    </Button>
                    <Button size="sm" onClick={approve} data-testid="request-approve-btn">
                      {t('pages.requests.approve')}
                    </Button>
                  </>
                )}
                {/* Wave B UAT — Climara campaign operational actions (hm_admin + hm_advisor) */}
                {open.request_type === 'climara_campaign' && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => { setOpen(null); navigate('/admin/climara'); }}
                            className="mr-auto" data-testid="climara-open-in-suite-btn">
                      <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                      {t('pages.requests.climara.openInClimara', { defaultValue: 'Open in Climara' })}
                    </Button>
                    {['pending', 'changes_requested'].includes(open.status) && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => setRejectOpen(true)}
                                disabled={climaraBusy} data-testid="climara-reject-btn">
                          {t('pages.requests.reject')}
                        </Button>
                        <Button variant="outline" size="sm" onClick={climaraRequestChanges}
                                disabled={climaraBusy} data-testid="climara-request-changes-btn">
                          {t('pages.requests.climara.requestChanges', { defaultValue: 'Request changes' })}
                        </Button>
                        <Button size="sm" onClick={climaraApprove} disabled={climaraBusy}
                                data-testid="climara-approve-launch-btn">
                          {t('pages.requests.climara.approveLaunch', { defaultValue: 'Approve & Launch' })}
                        </Button>
                      </>
                    )}
                  </>
                )}
                {/* Founder UAT — library review operational actions (hm_admin) */}
                {open.request_type === 'library_review' && (
                  <>
                    <Button variant="ghost" size="sm" className="mr-auto"
                            onClick={() => { setOpen(null); navigate(`/admin/studio/edit/${open.id}`); }}
                            data-testid="library-review-open-studio-btn">
                      <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                      {t('pages.requests.libraryReview.openStudio', { defaultValue: 'View in Assessment Studio' })}
                    </Button>
                    {isAdmin && open.status === 'submitted_for_review' && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => setRejectOpen(true)} disabled={libraryBusy}
                                data-testid="library-review-reject-btn">
                          {t('pages.requests.libraryReview.rejectReturn', { defaultValue: 'Reject (return to author)' })}
                        </Button>
                        <Button size="sm" onClick={libraryApprovePublish}
                                disabled={libraryBusy || !!libraryReadiness?.blocks_publish}
                                data-testid="library-review-approve-publish-btn">
                          {t('pages.requests.libraryReview.approvePublish', { defaultValue: 'Approve & Publish' })}
                        </Button>
                      </>
                    )}
                  </>
                )}
                {/* Founder UAT Wave 2 — cancellation decision (hm_admin + hm_advisor) */}
                {open.request_type === 'evaluation_request' && open.status === 'cancellation_requested' && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => decideCancellation(false)} data-testid="request-cancellation-reject-btn">
                      {t('pages.requests.rejectCancellation', { defaultValue: 'Keep request' })}
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => decideCancellation(true)} data-testid="request-cancellation-approve-btn">
                      {t('pages.requests.approveCancellation', { defaultValue: 'Approve cancellation' })}
                    </Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject reason dialog */}
      {/* TalentHeart 2026-06 — program-activation operational dialog */}
      <Dialog open={!!paDialog} onOpenChange={(v) => { if (!v) { setPaDialog(null); setPaText(''); } }}>
        <DialogContent data-testid="pa-action-dialog">
          <DialogHeader>
            <DialogTitle>
              {paDialog?.mode === 'info' && t('pages.requests.pa.requestInfoTitle', { defaultValue: 'Request information from the client' })}
              {paDialog?.mode === 'close_duplicate' && t('pages.requests.pa.closeDuplicateTitle', { defaultValue: 'Close request as duplicate' })}
              {paDialog?.mode === 'close_completed' && t('pages.requests.pa.closeCompletedTitle', { defaultValue: 'Close request as completed' })}
            </DialogTitle>
            <DialogDescription>
              {paDialog?.mode === 'info'
                ? t('pages.requests.pa.requestInfoHint', { defaultValue: 'The message is visible to the client and the request stays open as "Information required".' })
                : t('pages.requests.pa.closeHint', { defaultValue: 'The request will be closed. The full audit history is preserved and the client may submit a new request later.' })}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3}
            value={paText}
            onChange={(e) => setPaText(e.target.value)}
            placeholder={paDialog?.mode === 'info'
              ? t('pages.requests.pa.infoPlaceholder', { defaultValue: 'What information do you need from the client?' })
              : t('pages.requests.pa.reasonPlaceholder', { defaultValue: 'Reason (optional)' })}
            data-testid="pa-action-text"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setPaDialog(null); setPaText(''); }}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={paAction}
              disabled={paBusy || (paDialog?.mode === 'info' && !paText.trim())}
              data-testid="pa-action-confirm"
            >
              {t('common.confirm', { defaultValue: 'Confirm' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="reject-reason-dialog">
          <DialogHeader>
            <DialogTitle>{t('pages.requests.rejectReasonTitle')}</DialogTitle>
            <DialogDescription>{t('pages.requests.rejectReasonDesc')}</DialogDescription>
          </DialogHeader>
          <Input
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t('pages.requests.rejectReasonPh')}
            data-testid="reject-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={reject} data-testid="reject-reason-confirm">
              {t('pages.requests.reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 2D — Anular solicitud: confirmation + mandatory reason */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="cancel-request-dialog">
          <DialogHeader>
            <DialogTitle>{t('pages.requests.cancelTitle', { defaultValue: 'Anular solicitud' })}</DialogTitle>
            <DialogDescription>
              {t('pages.requests.cancelDesc', { defaultValue: 'La solicitud pasará al estado "Anulada": dejará de aparecer como trabajo pendiente, pero el registro completo y su historial se conservan y podrán consultarse con los filtros.' })}
            </DialogDescription>
          </DialogHeader>
          <Input
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder={t('pages.requests.cancelReasonPh', { defaultValue: 'Motivo de la anulación (obligatorio)' })}
            data-testid="cancel-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={cancelRequest} disabled={cancelReason.trim().length < 5} data-testid="cancel-request-confirm">
              {t('pages.requests.cancelAction', { defaultValue: 'Anular solicitud' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 41.37 — Temp password reveal (shown once after approval) */}
      <Dialog
        open={!!tempPasswordReveal}
        onOpenChange={(v) => { if (!v) setTempPasswordReveal(null); }}
      >
        <DialogContent data-testid="temp-password-dialog">
          <DialogHeader>
            <DialogTitle>
              {t('pages.requests.tempPassword.title', {
                defaultValue: 'Client admin user created',
              })}
            </DialogTitle>
            <DialogDescription>
              {t('pages.requests.tempPassword.desc', {
                defaultValue:
                  'Copy this password now — it will not be shown again. The user must change it on first login.',
              })}
            </DialogDescription>
          </DialogHeader>
          {tempPasswordReveal && (
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  {t('pages.requests.tempPassword.email', { defaultValue: 'Email' })}
                </p>
                <p className="font-medium" data-testid="temp-password-email">{tempPasswordReveal.email}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  {t('pages.requests.tempPassword.password', { defaultValue: 'Temporary password' })}
                </p>
                <div className="flex items-center gap-2">
                  <code className="px-3 py-2 rounded-md bg-muted font-mono text-sm flex-1" data-testid="temp-password-value">
                    {tempPasswordReveal.password}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard?.writeText(tempPasswordReveal.password);
                      toast.success(t('pages.requests.tempPassword.copied', { defaultValue: 'Copied' }));
                    }}
                    data-testid="temp-password-copy"
                  >
                    {t('common.copy', { defaultValue: 'Copy' })}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setTempPasswordReveal(null)} data-testid="temp-password-close">
              {t('common.close', { defaultValue: 'Close' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
